// The route profile of an own fleet card (R2616; v2-the-deep.md §18.1;
// ui-map-interface.md §8 "The route profile"; mockup round 088 card 05).
//
// A fleet with a course must show how deep its legs go against the depth
// rating of its hull. The profile is a section of the sea bed along the
// legs: the four bands as rows in the colours of card 09 over the water top
// (the depth light of R3229, `depthBandCss`), the bed as a step
// line by band, the depth rating as a dashed line, the part deeper than
// the rating in the bad ink, and the time until the hull passes the
// rating. The rating reads as a band (R2565): "rated to the slope".
//
// **IT READS THE LEGS, NOT THE PATH.** The wire states the legs of an own
// course (R2589); the router bends a leg round rock and, under `currents`,
// with the current (R2595), and the client mirrors no router. So the
// profile is the straight line from the hull through each leg, which the
// order line of the map draws too. A cell with no band (rock, or water
// the survey does not hold) breaks the line.
//
// **A FIXED COST.** The band is read every `sample_px` world px, at most
// `samples_max` times (`[presentation.route_profile]`), and the hull's own
// point is its cell centre, so the markup changes only when the hull
// enters a new cell and the card writes nothing on a frame it did not.

import { ROUTE_PROFILE } from '../../gen/presentation';
import { depthBandCss } from '../../depthlook';
import { card as S } from '../../gen/strings';
import type { Waypoint } from '../../gen/proto/aquaphobia';
import { BANDS, bandAt } from '../../deep';
import { cellAt, cellCenterX, cellCenterY, mpxToPx } from '../../world';
import { bandWord } from '../chart/deep-legend';
import { esc } from '../esc';

/** One point of the line the profile reads, in world px. */
export interface Pt { x: number; y: number }

/** One read of the band along the line. */
export interface Sample {
  /** World px along the line from the hull. */
  s: number;
  /** The band of the cell there, a `DepthBand`; 0 for no band. */
  band: number;
}

/** What the section draws. */
export interface Profile {
  samples: Sample[];
  /** The world px of the whole line. */
  length: number;
  /** The depth rating of the hull, a `DepthBand`. */
  rating: number;
  /** The deepest band along the line. */
  deepest: number;
  /** The world px along the line where the bed first goes deeper than the
   *  rating, or -1 when it never does. */
  belowAt: number;
}

/**
 * The line of an own course: the hull's cell centre, then each leg from
 * the one it sails to now. Null for no course.
 */
export function coursePoints(
  x: number, y: number, course: { legs: readonly Waypoint[]; leg: number } | undefined,
): Pt[] | null {
  if (!course || course.legs.length === 0) return null;
  const cell = cellAt(x, y);
  const pts: Pt[] = [cell >= 0 ? { x: cellCenterX(cell), y: cellCenterY(cell) } : { x, y }];
  const from = Math.min(Math.max(course.leg, 0), course.legs.length - 1);
  for (let i = from; i < course.legs.length; i++) {
    pts.push({ x: mpxToPx(course.legs[i].xMpx), y: mpxToPx(course.legs[i].yMpx) });
  }
  return pts;
}

/** The profile of a line over a band field, against a depth rating. */
export function routeProfile(depth: Uint8Array, pts: readonly Pt[], rating: number): Profile {
  let length = 0;
  for (let i = 1; i < pts.length; i++) length += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  const step = Math.max(ROUTE_PROFILE.samplePx, length / (ROUTE_PROFILE.samplesMax - 1));
  const samples: Sample[] = [];
  let deepest = 0, belowAt = -1;
  let leg = 1, legStart = 0;
  for (let s = 0; s <= length + 1e-6 && samples.length < ROUTE_PROFILE.samplesMax; s += step) {
    // Walk to the leg that holds `s`.
    while (leg < pts.length - 1) {
      const len = Math.hypot(pts[leg].x - pts[leg - 1].x, pts[leg].y - pts[leg - 1].y);
      if (s <= legStart + len) break;
      legStart += len;
      leg++;
    }
    const a = pts[leg - 1] ?? pts[0], b = pts[leg] ?? pts[0];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const t = len > 0 ? Math.min(1, (s - legStart) / len) : 0;
    const band = bandAt(depth, cellAt(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t));
    samples.push({ s, band });
    if (band > deepest) deepest = band;
    if (belowAt < 0 && rating > 0 && band > rating) belowAt = s;
  }
  return { samples, length, rating, deepest, belowAt };
}

/** One px figure with one place. */
function f1(v: number): string {
  return String(Math.round(v * 10) / 10);
}

/**
 * The section markup: the head with the rating, the picture and the foot.
 * `left` writes the time to cover `px` world px of the line, or '' for a
 * hull with no speed.
 */
export function routeProfileHtml(p: Profile, left: (px: number) => string): string {
  const W = ROUTE_PROFILE.wPx, H = ROUTE_PROFILE.hPx;
  const rowH = H / BANDS.length;
  const x = (s: number): number => (p.length > 0 ? (s / p.length) * W : 0);
  const yOf = (band: number): number => band * rowH;
  let rows = '';
  for (const b of BANDS) {
    rows += `<rect class="rp-band" x="0" y="${f1((b - 1) * rowH)}" width="${W}" height="${f1(rowH)}"`
      + ` style="--c:${depthBandCss(b, 'modern')}"/>`;
  }
  // The bed: one step line, broken where the field holds no band, and the
  // runs deeper than the rating again in the bad ink.
  let bed = '', bad = '';
  let pen = false, penBad = false;
  const n = p.samples.length;
  for (let i = 0; i < n; i++) {
    const smp = p.samples[i];
    const x0 = x(smp.s), x1 = i + 1 < n ? x(p.samples[i + 1].s) : W;
    if (smp.band === 0) { pen = false; penBad = false; continue; }
    const y = f1(yOf(smp.band));
    bed += `${pen ? 'L' : 'M'}${f1(x0)} ${y}L${f1(x1)} ${y}`;
    pen = true;
    if (p.rating > 0 && smp.band > p.rating) {
      bad += `${penBad ? 'L' : 'M'}${f1(x0)} ${y}L${f1(x1)} ${y}`;
      penBad = true;
    } else penBad = false;
  }
  const yr = f1(yOf(p.rating));
  const svg = `<svg class="ck-rp" viewBox="0 0 ${W} ${H}" aria-hidden="true">${rows}`
    + (bed ? `<path class="rp-bed" d="${bed}"/>` : '')
    + (bad ? `<path class="rp-bad" d="${bad}"/>` : '')
    + (p.rating > 0 ? `<path class="rp-rating" d="M0 ${yr}H${W}"/>` : '')
    + (p.belowAt >= 0 ? `<path class="rp-cross" d="M${f1(x(p.belowAt))} 0V${H}"/>` : '')
    + '</svg>';
  const rated = p.rating > 0 ? S.route_rated({ band: bandWord(p.rating, false) }) : '';
  const when = p.belowAt >= 0 ? left(p.belowAt) : '';
  const foot = p.belowAt >= 0
    ? `<span class="bad">${esc(when && p.belowAt > 0 ? S.route_below({ left: when }) : S.route_below_now)}</span>`
    : `<span>${esc(S.route_within)}</span>`;
  return '<div class="ck-sect ck-route"><div class="ck-sh">'
    + `<span class="ck-cap">${esc(S.route_head)}</span><span class="ck-shr">${esc(rated)}</span></div>`
    + svg
    + `<div class="ck-rfoot">${foot}`
    + (p.deepest > 0 ? `<span>${esc(S.route_deepest({ band: bandWord(p.deepest, false) }))}</span>` : '')
    + '</div></div>';
}

/** The depth fact of an own hull: its band now and its rating, or '' when
 *  the field holds no band at its cell or its class has no rating. */
export function depthFact(depth: Uint8Array | null, x: number, y: number, rating: number): string {
  const now = bandAt(depth, cellAt(x, y));
  if (now === 0 || rating === 0) return '';
  return S.depth_band({ band: bandWord(now, true), rating: bandWord(rating, true) });
}
